import { zodResolver } from '@hookform/resolvers/zod'
import { UserLoginSchema, userLoginSchema } from '@longy/clinic-schema'
import { SOCIAL_EMAIL_ACCOUNT_EXISTS_ERRORS, SocialEmailAccountExistsError } from '@longy/common'
import {
    authPillButtonClass,
    Button,
    Form,
    FormPasswordField,
    FormTextField,
    Icons,
} from '@longy/web-components'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { AuthLayout } from '../components/auth/AuthLayout'
import { SocialConsentStep } from '../components/auth/SocialConsentStep'
import { SocialLoginButtons } from '../components/auth/SocialLoginButtons'
import { api, getTRPCErrorMessage } from '../core/trpc'
import { useAuth } from '../hooks/useAuth'
import { getDeviceId, getDeviceName } from '../utils/device'
import { storeJwt } from '../utils/jwt'
import { safeRedirectTarget } from '../utils/redirect'
import { SocialSignInCandidate } from '../utils/socialLogin'

const PATH = '/login'

const loginSearchSchema = z.object({
    redirect: z.string().optional(),
    email: z.string().optional(),
    error: z.enum(SOCIAL_EMAIL_ACCOUNT_EXISTS_ERRORS).optional(),
})

const LoginPage = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const auth = useAuth()
    const { redirect, email: searchEmail, error: searchError } = Route.useSearch()

    const [error, setError] = useState<string | null>(searchError ? t(searchError) : null)
    const [awaitingEmailConfirmation, setAwaitingEmailConfirmation] = useState(false)
    const [socialCandidate, setSocialCandidate] = useState<SocialSignInCandidate | null>(null)

    // Social sign-in lands the person wherever the login gate pulled them away from, exactly as the
    // password path does — the two must not disagree about where "done" is.
    const onSocialSignedIn = useCallback(() => {
        navigate({ to: safeRedirectTarget(redirect) ?? '/' })
    }, [navigate, redirect])

    const form = useForm<UserLoginSchema>({
        resolver: zodResolver(userLoginSchema),
        defaultValues: { email: searchEmail ?? '', password: '', deviceId: '', deviceName: '' },
    })

    const onEmailAccountExists = useCallback(
        (email: string, errorKey: SocialEmailAccountExistsError) => {
            form.setValue('email', email)
            form.setFocus('password')
            setError(t(errorKey))
        },
        [form, t],
    )

    const { mutateAsync: login, isPending } = api.user.login.useMutation()
    const { mutateAsync: resendConfirmEmail } = api.user.resendConfirmEmail.useMutation()

    const onSubmit = async (data: UserLoginSchema) => {
        setError(null)

        try {
            const result = await login({
                ...data,
                deviceId: getDeviceId(),
                deviceName: getDeviceName(),
            })

            if (!result.emailConfirmed) {
                await resendConfirmEmail({ email: data.email }).catch(() => undefined)
                setAwaitingEmailConfirmation(true)
                return
            }

            if (result.is2FAEnabled) {
                // Stored before the redirect on purpose: sendLoginTwoFactorCode and
                // verifyLoginTwoFactorCode are authenticated procedures, so the next screen cannot
                // call either without this token. It is cleared again if the user leaves unverified.
                storeJwt(result.jwt)
                await navigate({ to: '/login/two-factor', search: { redirect } })
                return
            }

            await auth.login(result.jwt)
            await navigate({ to: safeRedirectTarget(redirect) ?? '/' })
        } catch (caught) {
            setError(getTRPCErrorMessage(caught))
        }
    }

    if (socialCandidate) {
        return (
            <SocialConsentStep
                candidate={socialCandidate}
                onSignedIn={onSocialSignedIn}
                onCancel={() => setSocialCandidate(null)}
            />
        )
    }

    if (awaitingEmailConfirmation) {
        return (
            <AuthLayout
                title={t('frontend.auth.awaitingEmailConfirmationTitle')}
                subtitle={t('frontend.auth.awaitingEmailConfirmationBody')}
            />
        )
    }

    return (
        <AuthLayout
            title={t('frontend.auth.loginTitle')}
            footer={
                <>
                    {t('frontend.auth.noAccountYet')}{' '}
                    <Link className="text-primary underline" to="/register" search={{ redirect }}>
                        {t('frontend.auth.createAccount')}
                    </Link>
                </>
            }
        >
            <Form {...form}>
                <FormTextField
                    control={form.control}
                    name="email"
                    label={t('frontend.auth.email')}
                    inputProps={{ type: 'email', autoComplete: 'email', disabled: isPending }}
                />
                <FormPasswordField
                    control={form.control}
                    name="password"
                    label={t('frontend.auth.password')}
                    inputProps={{ autoComplete: 'current-password', disabled: isPending }}
                />
            </Form>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button
                className={authPillButtonClass}
                disabled={isPending}
                onClick={form.handleSubmit(onSubmit)}
            >
                {isPending && <Icons.Spinner className="mr-2 h-4 animate-spin" />}
                {t('frontend.auth.login')}
            </Button>

            <Link
                className="text-center text-sm text-brand-gray underline hover:text-primary"
                to="/forgot-password"
            >
                {t('frontend.auth.forgotPassword')}
            </Link>

            <SocialLoginButtons
                disabled={isPending}
                onSignedIn={onSocialSignedIn}
                onNeedsConsent={setSocialCandidate}
                onError={setError}
                onEmailAccountExists={onEmailAccountExists}
            />
        </AuthLayout>
    )
}

export const Route = createFileRoute(PATH)({
    // The authenticated surface never renders on the server: getJwt() reads localStorage, so an SSR
    // pass has no token to send and every authed query would fail there anyway.
    ssr: false,
    validateSearch: loginSearchSchema,
    head: () => ({ meta: [{ title: 'Long-Y - Bejelentkezés' }] }),
    component: LoginPage,
})
